import { Link } from 'react-router-dom';
import { GlobalNavigation } from '@/components/global-navigation';
import { LandingFooter } from '@/components/landing/landing-footer';
import { SEOHead } from '@/components/seo/SEOHead';
import { SKILL_PAGES } from '@/config/skill-pages';
import { Users, ArrowRight } from 'lucide-react';

const categories = Object.entries(SKILL_PAGES).map(([slug, config]) => ({
  slug,
  ...config,
}));

export default function ExpertsIndexPage() {
  return (
    <>
      <SEOHead
        title="Browse AI Experts by Specialty"
        description="Find vetted AI experts across 15+ specialties. Claude Code developers, AI agents, machine learning engineers, prompt engineers, and more. All experts are reviewed and vetted."
        canonicalUrl="https://aiexpertnetwork.com/experts"
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Browse AI Experts by Specialty',
          description: 'Find vetted AI experts across 15+ specialties on AI Expert Network.',
          url: 'https://aiexpertnetwork.com/experts',
          provider: {
            '@type': 'Organization',
            name: 'AI Expert Network',
            url: 'https://aiexpertnetwork.com',
          },
        }}
      />
      <GlobalNavigation />
      <div className="min-h-screen bg-slate-50">
        <div
          className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8"
          style={{
            background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Browse AI Experts by Specialty
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
              AI Expert Network connects businesses with vetted AI consultants and developers across 15+ specialties. Every expert is reviewed before joining the platform.
            </p>
            <p className="text-sm text-slate-500 max-w-xl mx-auto bg-white/60 rounded-xl px-4 py-3 border border-slate-200/50">
              Find the right AI expert for your project. Browse by skill, view profiles, and start a conversation. Rates range from $70-300/hr depending on specialization.
            </p>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/experts/${cat.slug}`}
                className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:border-blue-300 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                    {cat.title}
                  </h2>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
                <p className="text-sm text-slate-500 line-clamp-2 mb-3">{cat.description}</p>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Users className="h-3 w-3" />
                  <span>Browse experts</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <LandingFooter />
    </>
  );
}
